import Vue from 'vue'
import { DeliveryPeriod } from '@/api'
import isEqual from 'lodash.isequal'
import { Query } from '@/modules/plugins/query'
import mixins from '@/modules/mixins/global'
import { tierTimeState } from '@/modules/helpers/deliveryPeriodSlots'

let deliveryPeriodParams, deliveryPeriod
// task #155: the request in flight ({ params, promise, seq }) and the number of the latest request
let pending = null
let sequence = 0

// task #155: id of the restaurant the cart belongs to
function cartRestaurantId() {
    return mixins.getId(Vue.prototype.$cart?.calculation?.restaurant)
}

// task #155 (spec T8): tier input, only the requested restaurant's own cart counts
function cartSubtotalFor(restaurantId) {
    return cartRestaurantId() === restaurantId
        ? Vue.prototype.$cart?.calculation?.price_sum_items || 0
        : 0
}

const state = {
    deliveryPeriod: {},
    deliveryFirstAvailable: {},
    // task #155: what the committed periods were computed for
    committedRestaurantId: null,
    committedPrepValue: null,
    committedAllowanceId: null,
    lastFetchFailed: false,
    // tasks#170: number of period requests on their way (the committed periods may be replaced soon)
    fetchesInFlight: 0,
}
const mutations = {
    SET_DELIVERY_PERIOD(state, payload) {
        state.deliveryPeriod = payload
    },
    SET_DELIVERY_FIRST_AVAILABLE(state, payload) {
        if (!payload?.days) {
            state.deliveryFirstAvailable = {}
            return
        }
        const day = Object.values(payload.days).find((d) => d && d.length)
        state.deliveryFirstAvailable = day
            ? day.find((range) => Vue.moment().isBefore(range?.from))?.from
            : {}
    },
    SET_COMMITTED_PARAMS(state, { restaurantId, prepValue, allowanceId }) {
        state.committedRestaurantId = restaurantId
        state.committedPrepValue = prepValue
        state.committedAllowanceId = allowanceId
    },
    SET_LAST_FETCH_FAILED(state, payload) {
        state.lastFetchFailed = payload
    },
    CHANGE_FETCHES_IN_FLIGHT(state, delta) {
        state.fetchesInFlight = Math.max(0, state.fetchesInFlight + delta)
    },
}

const actions = {
    async getDeliveryPeriod(
        { state, commit, getters, dispatch },
        { restaurantId, allowanceId, force = false }
    ) {
        const params = {
            restaurant_id: restaurantId,
            allowance_id: allowanceId,
            order_value: restaurantId
                ? Vue.prototype.$cart?.calculation?.price_sum_items || 0
                : undefined,
            // task #155: tier input, only this restaurant's own cart counts (spec T8)
            preparation_order_value: restaurantId ? cartSubtotalFor(restaurantId) : undefined,
            ...Query.getLocationParams(getters['order/address'], true),
        }
        if (!deliveryPeriod) deliveryPeriod = new DeliveryPeriod()
        // the same request is already running: every caller waits for it
        if (pending && isEqual(pending.params, params)) return pending.promise
        if (!pending && isEqual(deliveryPeriodParams, params) && !force) return
        deliveryPeriodParams = params
        const seq = ++sequence
        commit('CHANGE_FETCHES_IN_FLIGHT', 1)
        const promise = (async () => {
            try {
                const result = await deliveryPeriod.fetch(undefined, params)
                // a newer request was started meanwhile: never let an older answer overwrite it
                if (seq !== sequence) return result
                // the API client resolves a network-level / unparsable failure as undefined: for committed tiered
                // periods treat it as a failed fetch (the catch keeps the old periods, flags the failure and
                // allows a retry), so the tier guard stays on; untiered periods keep committing it (spec U1)
                if (
                    (!result || typeof result.days !== 'object' || result.days === null) &&
                    state.deliveryPeriod &&
                    state.deliveryPeriod.preparation_time != null
                ) {
                    throw new Error('deliveryperiod: empty response')
                }
                commit('SET_DELIVERY_PERIOD', result)
                commit('SET_DELIVERY_FIRST_AVAILABLE', result)
                commit('SET_COMMITTED_PARAMS', {
                    restaurantId,
                    prepValue: params.preparation_order_value,
                    allowanceId: params.allowance_id,
                })
                commit('SET_LAST_FETCH_FAILED', false)
                // tiered restaurant whose answer was computed for another subtotal (first load, quick edits):
                // the caller waits for the corrected answer too
                if (
                    restaurantId &&
                    result?.preparation_time != null &&
                    cartSubtotalFor(restaurantId) !== params.preparation_order_value
                ) {
                    pending = null
                    return dispatch('getDeliveryPeriod', { restaurantId, allowanceId })
                }
                return result
            } catch (e) {
                if (seq === sequence) {
                    deliveryPeriodParams = undefined // allow the next call to retry
                    commit('SET_LAST_FETCH_FAILED', true)
                }
                throw e
            } finally {
                if (pending && pending.seq === seq) pending = null
                commit('CHANGE_FETCHES_IN_FLIGHT', -1)
            }
        })()
        pending = { params, promise, seq }
        return promise
    },
}

const getters = {
    deliveryPeriod: (state) => JSON.parse(JSON.stringify(state.deliveryPeriod || {})),
    deliveryFirstAvailable: (state) => state.deliveryFirstAvailable,
    // tasks#170: a period request is on its way
    deliveryPeriodLoading: (state) => state.fetchesInFlight > 0,
    // task #155: the committed periods are tiered, belong to the cart's restaurant and were computed for
    // another subtotal than the cart's current one (subtotal change, or the first calculate of a reloaded cart
    // arrived after the first period answer)
    deliveryPeriodTierOutdated: (state) => {
        const restaurantId = cartRestaurantId()
        return (
            state.deliveryPeriod?.preparation_time != null &&
            !!restaurantId &&
            state.committedRestaurantId === restaurantId &&
            state.committedPrepValue !== cartSubtotalFor(restaurantId)
        )
    },
    // task #155 (spec §3.5): 'ok' | 'outdated' | 'unavailable'
    tierTimeState: (state, getters, rootState, rootGetters) => {
        const restaurantId = cartRestaurantId()
        return tierTimeState({
            period: state.deliveryPeriod,
            committedRestaurantId: state.committedRestaurantId,
            committedPrepValue: state.committedPrepValue,
            lastFetchFailed: state.lastFetchFailed,
            cartRestaurantId: restaurantId,
            cartSubtotal: cartSubtotalFor(restaurantId),
            isAsap: rootGetters['order/asap'],
            time: rootGetters['order/time'],
            isCanteen: !!Vue.prototype.$cart?.isCanteen,
        })
    },
}

export default {
    state,
    mutations,
    actions,
    getters,
}
